import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { EnsoProvider, PageSpinner } from '@ohpen/enso-ui';
import { ConfigContext, getCdnAssets, type IConfig } from '@ohpen/react-utils';
import { AuthProvider, PrivateRoute } from '@ohpen/react-auth';

import { AuthenticatedContent } from './AuthenticatedContent';
import { getOidcConfig } from './getOidcConfig';
import { Login } from './Login';

const THEME_LOAD_TIMEOUT_MS = 5000;

export const App = ({ config }: { config: IConfig }) => {
  const tenantClient = config?.CLIENT || 'ohp';
  const assetsBaseUrl = config?.THEMING?.ASSETS_BASE_URL || 'https://enso-ui-cdn.ohpen.tech';
  const oidcConfig = getOidcConfig(config);
  const [themeLoaded, setThemeLoaded] = useState(false);

  const amplifyConfig = {
    enableCredentials: config?.AWS?.ENABLE_CREDENTIALS_LOGIN ?? false,
    userPoolId: config.AWS.USER_POOL_ID,
    userPoolClientId: config.AWS.USER_POOL_APP_CLIENT_ID,
  };

  useEffect(() => {
    let cancelled = false;
    const markLoaded = () => {
      if (!cancelled) setThemeLoaded(true);
    };
    // Render with default theming rather than hanging if the theme CSS never loads.
    getCdnAssets(config).then(markLoaded).catch(markLoaded);
    const timeoutId = window.setTimeout(markLoaded, THEME_LOAD_TIMEOUT_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [config]);

  return (
    <ConfigContext.Provider value={config}>
      <EnsoProvider client={tenantClient} assetsBaseUrl={assetsBaseUrl}>
        <AuthProvider oidcConfig={oidcConfig} amplifyConfig={amplifyConfig}>
          <BrowserRouter>
            {themeLoaded ? (
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="*"
                  element={
                    <PrivateRoute>
                      <AuthenticatedContent />
                    </PrivateRoute>
                  }
                />
              </Routes>
            ) : (
              <PageSpinner />
            )}
          </BrowserRouter>
        </AuthProvider>
      </EnsoProvider>
    </ConfigContext.Provider>
  );
};
